import { Component, ComponentInterface, Element, h, Host, Listen, Prop, State, Watch } from '@stencil/core';
import { debounceTime, Subject, takeUntil } from 'rxjs';

@Component({
  tag: 'vds-menu',
  styleUrl: 'vds-menu.scss',
  shadow: true,
})
export class VdsMenu implements ComponentInterface {

  private desktopMediaQuery = `(min-width: ${getComputedStyle(document.documentElement).getPropertyValue('--vt-breakpoint-md').trim()})`;

  @Element() el!: HTMLVdsMenuElement;

  /**
   * Determines the dropdown menu state
   */
  @Prop({ mutable: true, reflect: true }) open: boolean = false;

  /**
   *  Dropdown trigger behaviour modes
   */
  @Prop() mode: 'hover' | 'click' = 'hover';

  /**
   * 	Delay time in milliseconds before a dropdown is displayed in hover mode.
   */
  @Prop() delayShow: number = 0;

  /**
   * Delay time in milliseconds before a dropdown is hidden in hover mode.
   */
  @Prop() delayHide: number = 800;

  /**
   * Makes sure the menu sticks to the top if set to true
   */
  @Prop({ mutable: true, reflect: true }) fixed: boolean = false;

  /**
   * Sets the body overflow on the Y-Axis to false when menu is open
   */
  @Prop() disableScroll: boolean = false;

  /**
   * Tracks the active menu item index
   */
  @State() activeIndex?: number;

  @Watch('activeIndex')
  protected activeIndexChanged() {
    if(this.activeIndex != undefined) {
      this.toggleSubmenu();
    }
  }

  /**
   * Desktop Media-Query State
   * is true when viewport matches desktop media-query
   */
  @State() isDesktop = window.matchMedia(this.desktopMediaQuery).matches;

  @Watch('isDesktop')
  protected breakpointChanged() {
    if (!this.isDesktop) {
      this.open = false;
    }
  }

  @Watch('open')
  handleMenuOpening() {
    const stages = this.el.querySelectorAll('vds-submenu-stage');
    stages.forEach((stage) => {
      stage.open = !stage.open;
    });
    document.body.style.overflowY = this.open && this.disableScroll ? "hidden" : "scroll";
  }

  @Listen('resize', { target: 'window' })
  handleResize() {
    this.isDesktop = window.matchMedia(this.desktopMediaQuery).matches;
  }

  @Listen('click', { capture: true, target: 'document' })
  handleClick(event: Event) {
    const target = event.target as HTMLElement;
    const isDescendant = this.el.contains(target);
    const isSelf = target === this.el;
    if (!isDescendant || isSelf) {
      this._open = false;
      this.mouseLeave$.next();
      this.updateActiveNavitem();
    }
  }

  private _open: boolean = false;

  private navItems: HTMLVdsInteractiveLinkElement[];

  private mouseEnter$ = new Subject<void>();

  private mouseLeave$ = new Subject<void>();

  private destroy$ = new Subject<void>();

  componentWillRender(): void {
    this.watchDropdownChange();
  }

  render() {
    const { open } = this;
    return (
      <Host>
        <div class={{
          "hidden md:flex justify-center w-full bg-white border-t-[10px] border-t-yellow border-b border-b-grey-lighter px-[50px]": true
        }}>
          <nav>
            <div>
              <slot name="logo"></slot>
            </div>
            <div class="flex">
              <div class="flex">
                <slot name="dropdown-items"></slot>
              </div>
              <ul class="flex list-none">
                <slot name="nav-items"></slot>
              </ul>
            </div>
          </nav>
        </div>

        <div
          class={{
            'absolute flex justify-center w-full transition-all duration-[.2s] ease-linear bg-white border-b border-b-grey-lighter shadow-md overflow-hidden max-h-0 px-10': true,
            'max-h-[547px]': open,
          }}
          onMouseEnter={e => this.handleDropdownMouseEnter(e)}
          onMouseLeave={e => this.handleDropdownMouseLeave(e)}
        >
          <div class="submenu-wrapper">
            <slot name="submenu"></slot>
          </div>
        </div>
      </Host>
    );
  }

  componentDidRender(): void {
    this.watchNavItems();
  }

  private watchDropdownChange(): void {
    this.mouseEnter$.pipe(debounceTime(this.delayShow), takeUntil(this.destroy$)).subscribe(() => {
      if (this._open) {
        this.toggleDropdown(true);
      }
    });

    this.mouseLeave$.pipe(debounceTime(this.delayHide), takeUntil(this.destroy$)).subscribe(() => {
      if (!this._open) {
        this.updateActiveNavitem();
        this.toggleDropdown(false);
      }
    });
  }

  async disconnectedCallback(): Promise<void> {
    this.destroy$.next();

    this.navItems.forEach(i => {
      i.removeEventListener('click', (e: Event) => this.handleNavitemMouseClick(e, i));
      i.removeEventListener('mouseenter', (e: Event) => this.handleNavitemMouseEnter(e, i));
      i.removeEventListener('mouseout', (e: Event) => this.handleNavitemMouseLeave(e, i));
    });
  }

  private watchNavItems(): void {
    const items = (this.navItems = Array.from(this.el.querySelectorAll('vds-interactive-link:not([no-submenu])')));
    items.forEach(i => {
      i.addEventListener('click', (e: Event) => this.handleNavitemMouseClick(e, i));
      i.addEventListener('mouseenter', (e: Event) => this.handleNavitemMouseEnter(e, i));
      i.addEventListener('mouseout', (e: Event) => this.handleNavitemMouseLeave(e, i));
    });
  }

  private handleNavitemMouseClick(event: Event, item: HTMLVdsInteractiveLinkElement): void {
    const active = this.navItems.find(x => x === item).active;

    if (this.proceed(event.type)) {
      if (!active) {
        event.preventDefault();
        this._open = true;
        this.mouseEnter$.next();
        this.updateActiveNavitem(item);
      }
    }
  }

  private handleNavitemMouseEnter(event: Event, item: HTMLVdsInteractiveLinkElement): void {
    if (this.proceed(event.type)) {
      this._open = true;
      this.mouseEnter$.next();
      this.updateActiveNavitem(item);
    }
  }

  private handleNavitemMouseLeave(event: Event, _item: HTMLVdsInteractiveLinkElement): void {
    if (this.proceed(event.type)) {
      this._open = false;
      this.mouseLeave$.next();
    }
  }

  private handleDropdownMouseEnter(_event: Event): void {
    this._open = true;
  }

  private handleDropdownMouseLeave(event: Event): void {
    if (this.proceed(event.type)) {
      this._open = false;
      this.mouseLeave$.next();
    }
  }

  private proceed(eventType: string, mode: 'click' | 'hover' = this.mode): boolean {
    if (eventType === 'mouseenter' || eventType === 'mouseout' || eventType === 'mouseleave') {
      if (mode === 'hover') return true;
      else return false;
    } else if (eventType === 'click') {
      if (mode === 'click') return true;
      else return false;
    }
  }

  private toggleDropdown(status: boolean = this._open): void {
    this.open = status;
  }

  private updateActiveNavitem(item?: HTMLVdsInteractiveLinkElement): void {
    this.navItems.forEach(navItem => navItem.active = navItem === item);
    const activeIndex = this.navItems.findIndex(x => x.active);
    this.activeIndex = activeIndex > -1 ?
      activeIndex :
      undefined;
  }

  private toggleSubmenu(): void {
    const items = Array.from(this.el.querySelectorAll<HTMLVdsSubmenuElement>(':scope > vds-submenu'));
    items.forEach((item, i) => {
      item.hidden = i !== this.activeIndex;
    });
  }
}
